import { useMemo, useState } from "react";
import { Search, MapPin, Users, CalendarClock, Clock, Navigation } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import GKKMap, { directionsUrl } from "@/components/GKKMap";
import { gkkDirectory } from "@/data/gkkDirectory";
import { nextMassForGKK, todaysGkkMasses } from "@/data/gkkSchedule";

function normalize(s) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export default function GKKDirectory() {
  const [q, setQ] = useState("");
  const [zone, setZone] = useState("All");
  const [mapGkk, setMapGkk] = useState(null);
  const showOnMap = (g) => {
    setMapGkk(g);
    document.getElementById("gkk-map")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const zones = useMemo(() => {
    const uniq = [...new Set(gkkDirectory.map((g) => g.zone).filter(Boolean))];
    // Natural sort: "Zone 1", "Zone 2", ...
    uniq.sort((a, b) => {
      const na = parseInt(a.match(/\d+/)?.[0] || "0", 10);
      const nb = parseInt(b.match(/\d+/)?.[0] || "0", 10);
      return na - nb;
    });
    return uniq;
  }, []);

  const filtered = useMemo(() => {
    const nq = normalize(q.trim());
    return gkkDirectory.filter((g) => {
      if (zone !== "All" && g.zone !== zone) return false;
      if (!nq) return true;
      const hay = normalize(`${g.patron} ${g.venue} ${g.address} ${g.zone || ""}`);
      return hay.includes(nq);
    });
  }, [q, zone]);

  const total = gkkDirectory.length;
  const todays = useMemo(() => todaysGkkMasses(), []);

  // Count per zone (used inside chip)
  const countsByZone = useMemo(() => {
    const map = { All: gkkDirectory.length };
    for (const g of gkkDirectory) {
      map[g.zone] = (map[g.zone] || 0) + 1;
    }
    return map;
  }, []);

  return (
    <section
      id="gkk"
      data-testid="gkk-directory"
      className="py-24 md:py-36 bg-parchment-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel
          kicker="Gagmay'ng Kristohanong Katilingban"
          title="Our GKK Directory"
          intro={`Fifty Basic Ecclesial Communities — the living cells of the parish. Search by patron saint or location to find the GKK celebrating Mass near you.`}
        />

        {/* Today's GKK Masses ribbon — shows live, with a graceful empty state */}
        <div
          data-testid="gkk-today-ribbon"
          className="mt-12 bg-cocoa-900 text-parchment-100 px-6 md:px-8 py-5 md:py-6 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_30%,rgba(212,175,55,0.18),transparent_60%)] pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex items-center gap-3 flex-shrink-0">
              <Clock size={16} className="text-gold" aria-hidden />
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-gold font-sans">
                  Today
                </div>
                <div className="font-serif italic text-lg leading-none mt-1">
                  GKK Masses
                </div>
              </div>
            </div>

            <div className="hidden md:block w-px self-stretch bg-parchment-100/15" />

            <div className="flex-1">
              {todays.length === 0 ? (
                <div className="font-sans italic text-sm text-parchment-200/80">
                  No GKK Masses scheduled in chapels today. Please join the
                  Holy Eucharist at the Parish Church.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {todays.map((m, i) => (
                    <span
                      key={`${m.time}-${m.patron}-${m.venue}-${i}`}
                      data-testid="gkk-today-chip"
                      className="inline-flex items-baseline gap-2 bg-parchment-100/8 hover:bg-parchment-100/15 border border-parchment-100/15 px-3 py-1.5 transition-colors"
                    >
                      <span className="font-serif text-base text-parchment-100 leading-none">
                        {m.time}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-gold leading-none">
                        {m.patron}
                      </span>
                      {m.venue && (
                        <span className="text-[11px] italic text-parchment-200/80 leading-none">
                          · {m.venue}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter by Zone chip row */}
        <div
          data-testid="gkk-zone-filter"
          className="mt-10 flex flex-wrap items-center gap-2"
        >
          <span className="text-[10px] uppercase tracking-[0.28em] text-cocoa-700/70 font-sans mr-2">
            Filter · Zone
          </span>
          {["All", ...zones].map((z) => {
            const active = zone === z;
            return (
              <button
                key={z}
                type="button"
                onClick={() => setZone(z)}
                data-testid={`gkk-zone-chip-${z.replace(/\s+/g, "-").toLowerCase()}`}
                aria-pressed={active}
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 border font-sans text-[11px] uppercase tracking-[0.22em] transition-colors ${
                  active
                    ? "bg-cocoa-900 text-parchment-100 border-cocoa-900"
                    : "bg-parchment-100 text-cocoa-800 border-cocoa-900/15 hover:border-terracotta hover:text-terracotta"
                }`}
              >
                <span>{z}</span>
                <span
                  className={`text-[9px] tracking-normal ${
                    active ? "text-gold" : "text-cocoa-700/60"
                  }`}
                >
                  {countsByZone[z] || 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search bar */}
        <GKKMap selected={mapGkk} onClear={() => setMapGkk(null)} />
        <div className="mt-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div className="relative flex-1 max-w-2xl">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-cocoa-700/60"
              aria-hidden
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by patron saint or village…"
              aria-label="Search GKK directory"
              data-testid="gkk-search"
              className="w-full bg-parchment-100 border border-cocoa-900/15 pl-12 pr-4 py-3.5 font-sans text-[15px] text-cocoa-900 placeholder:text-cocoa-700/50 focus:outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta/40 transition-colors"
            />
          </div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-sage-dark font-sans">
            <Users size={14} />
            <span data-testid="gkk-count">
              {filtered.length} of {total} GKKs
            </span>
          </div>
        </div>

        {/* Results grid */}
        {filtered.length === 0 ? (
          <div
            data-testid="gkk-empty"
            className="mt-16 border border-dashed border-cocoa-900/15 p-10 text-center font-sans text-cocoa-700 italic"
          >
            No GKK matches your search
            {zone !== "All" ? ` in ${zone}` : ""}. Try a different patron saint,
            location, or zone.
          </div>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cocoa-900/10">
            {filtered.map((g, i) => {
              const next = nextMassForGKK(g);
              return (
              <article
                key={`${g.patron}-${g.venue}-${i}`}
                data-testid="gkk-card"
                className="group bg-parchment-50 p-6 hover:bg-parchment-100 transition-colors relative"
              >
                {g.badge && (
                  <div className="absolute top-0 right-6 text-[8.5px] uppercase tracking-[0.24em] text-gold bg-cocoa-900 px-2 py-0.5">
                    {g.badge}
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="mt-1 w-8 h-8 flex-shrink-0 border border-terracotta/40 text-terracotta font-serif text-sm flex items-center justify-center">
                    ✦
                  </div>
                  <div className="min-w-0 w-full">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans">
                        GKK
                      </span>
                      {g.zone && (
                        <span className="text-[9px] uppercase tracking-[0.22em] text-cocoa-700 bg-parchment-200 px-2 py-0.5">
                          {g.zone}
                        </span>
                      )}
                    </div>
                    <h3 className="font-serif text-[1.15rem] text-cocoa-900 leading-snug">
                      {g.patron}
                    </h3>
                    <div className="mt-3 flex items-start gap-1.5 text-sm text-cocoa-700 font-sans">
                      <MapPin
                        size={13}
                        className="mt-1 flex-shrink-0 text-cocoa-700/70"
                        aria-hidden
                      />
                      <span data-testid="gkk-address" className="leading-snug">{g.address}</span>
                    </div>

                    {/* Next Mass — pulled live from the parish calendar */}
                    <div
                      data-testid="gkk-next-mass"
                      className="mt-4 pt-3 border-t border-cocoa-900/10 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-sans"
                    >
                      <CalendarClock
                        size={13}
                        className={
                          next ? "text-terracotta" : "text-cocoa-700/40"
                        }
                        aria-hidden
                      />
                      {next ? (
                        <span className="text-cocoa-800">
                          <span className="text-sage-dark">Next Mass:</span>{" "}
                          <span className="text-cocoa-900 font-medium tracking-[0.12em]">
                            {next.weekdayShort} · {next.time}
                          </span>
                        </span>
                      ) : (
                        <span className="text-cocoa-700/60 italic normal-case tracking-normal">
                          Next Mass to be announced
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] font-sans">
                      <button
                        type="button"
                        data-testid="gkk-show-on-map"
                        onClick={() => showOnMap(g)}
                        className="inline-flex items-center gap-1.5 text-terracotta hover:text-cocoa-900 transition-colors"
                      >
                        <MapPin size={12} /> Show on map
                      </button>
                      <a
                        href={directionsUrl(g)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="gkk-directions"
                        className="inline-flex items-center gap-1.5 text-cocoa-700 hover:text-terracotta transition-colors"
                      >
                        <Navigation size={12} /> Directions
                      </a>
                    </div>
                  </div>
                </div>
              </article>
              );
            })}
          </div>
        )}

        {/* Footnote */}
        <p className="mt-12 text-xs text-cocoa-700/70 font-sans italic">
          Compiled from the parish Mass schedule. Anointed by the prayers of the
          parishioners, each GKK is a domestic Church living the Gospel where it
          stands.
        </p>
      </div>
    </section>
  );
}
